
import React, { useEffect, useState, useRef } from 'react';
import { X, BookOpen, Facebook, Instagram, Youtube, Twitter, Send, Music, Info, MessageSquare, ChevronDown, ChevronUp, PlayCircle, Sparkles, Users, Compass, Scan, Lock, Heart, Book, Quote, ArrowUp, ArrowDown, Settings, Eye, EyeOff, RotateCcw, Moon, FileText, StickyNote, ImageIcon, Folder, ShoppingBag, ExternalLink, Music2, Volume2, VolumeX, Play, Pause, SkipBack, SkipForward, Repeat, Palette } from 'lucide-react';
import { Tab, SocialLinks, AUDIO_CATEGORIES, SidebarMenuItem, AlbumCategory, DEFAULT_ALBUM_CATEGORIES } from '../types';
import { getSocialLinks, fetchSocialLinksFromServer, getEbookCategories, getSidebarMenu, moveSidebarItem, toggleSidebarItemVisibility, resetSidebarMenu } from '../services/mockService';
import { useLanguage } from './LanguageContext';
import { useMusic } from '../context/MusicContext';
import { PWAInstallButton } from './PWAInstallButton';

interface Props {
  isOpen: boolean; 
  onClose: () => void;
  onNavigate: (tab: Tab) => void;
  onNavigateAudio: (category: string | null) => void;
  onNavigateEbook: (category: string | null) => void;
  onNavigateAlbum?: (category: string | null) => void;
  onOpenEkadashi?: () => void;
  onOpenBadges?: () => void;
  onOpenCommunity?: () => void;
  onOpenReminders?: () => void;
  isAdmin?: boolean;
}

// Icon mapping
const iconMap: Record<string, React.ReactNode> = {
  Sparkles: <Sparkles size={20} className="text-saffron animate-pulse" />,
  Quote: <Quote size={20} className="text-saffron" />,
  Moon: <Moon size={20} className="text-saffron" />,
  Heart: <Heart size={20} className="text-saffron" />,
  Scan: <Scan size={20} className="text-saffron" />,
  Lock: <Lock size={20} className="text-saffron" />,
  Compass: <Compass size={20} className="text-saffron" />,
  Users: <Users size={20} className="text-saffron" />,
  BookOpen: <BookOpen size={20} className="text-saffron" />,
  Music: <Music size={20} className="text-saffron" />,
  Info: <Info size={20} className="text-saffron" />,
  MessageSquare: <MessageSquare size={20} className="text-saffron" />,
  FileText: <FileText size={20} className="text-saffron" />,
  StickyNote: <StickyNote size={20} className="text-saffron" />,
  ImageIcon: <ImageIcon size={20} className="text-saffron" />,
  PlayCircle: <PlayCircle size={20} className="text-saffron" />,
  ShoppingBag: <ShoppingBag size={20} className="text-saffron" />,
  Palette: <Palette size={20} className="text-saffron" />,
  Eye: <Eye size={20} className="text-saffron" />,
  Book: <Book size={20} className="text-saffron" />,
};

const STORAGE_KEY_ALBUM_CATEGORIES = 'ram_naam_album_categories';

export const StoreSidebar: React.FC<Props> = ({ isOpen, onClose, onNavigate, onNavigateAudio, onNavigateEbook, onNavigateAlbum, onOpenEkadashi, onOpenBadges, onOpenCommunity, onOpenReminders, isAdmin }) => {
  const [links, setLinks] = useState<SocialLinks | null>(null);
  const [showAudioCategories, setShowAudioCategories] = useState(false);
  const [showEbookCategories, setShowEbookCategories] = useState(false);
  const [showAlbumCategories, setShowAlbumCategories] = useState(false);
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  const [ebookCategories, setEbookCategories] = useState<string[]>([]);
  const [albumCategories, setAlbumCategories] = useState<AlbumCategory[]>([]);
  const [menuItems, setMenuItems] = useState<SidebarMenuItem[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const { t, language } = useLanguage();
  const { tracks, currentTrack, isPlaying, volume, isLooping, toggle, setTrack, setVolume, setLooping, nextTrack, prevTrack } = useMusic();
  
  // Translation key mapping for sidebar items
  const labelKeyMap: Record<string, string> = {
    'ai_vani': 'nav_ai_vani',
    'sadhak_test': 'nav_atma_parikshan',
    'favorites': 'nav_favorites',
    'notes': 'nav_notes',
    'videos': 'nav_videos',
    'poems': 'nav_poems',
    'shloka_day': 'nav_shloka',
    'ekadashi': 'nav_ekadashi',
    'blogs': 'nav_blogs',
    'buy_books': 'nav_buy_books',
    'scanner': 'nav_scanner',
    'insights': 'nav_insights',
    'sangha': 'nav_sangha',
    'ebooks': 'nav_ebooks',
    'audios': 'nav_audios',
    'leela_chintan': 'nav_leela_chintan'
  };

  const getItemLabel = (item: SidebarMenuItem) => {
    const key = labelKeyMap[item.id];
    return key ? t(key as any) : item.label;
  };
  
  const scrollYRef = useRef(0);
  const sidebarContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLinks(getSocialLinks());
      fetchSocialLinksFromServer().then(s => setLinks(s)).catch(() => {});
      setEbookCategories(getEbookCategories());
      setMenuItems(getSidebarMenu());
      
      // Load album categories
      const storedAlbumCategories = localStorage.getItem(STORAGE_KEY_ALBUM_CATEGORIES);
      if (storedAlbumCategories) {
        try {
          setAlbumCategories(JSON.parse(storedAlbumCategories));
        } catch (e) {
          setAlbumCategories(DEFAULT_ALBUM_CATEGORIES);
        }
      } else {
        setAlbumCategories(DEFAULT_ALBUM_CATEGORIES);
      }
      
      // Capture current scroll position
      scrollYRef.current = window.scrollY;
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
    } else {
      setShowAudioCategories(false);
      setShowEbookCategories(false);
      setShowAlbumCategories(false);
      setShowMusicPlayer(false);
      setIsEditMode(false);
      
      // Restore body scroll
      const scrollY = scrollYRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  // Prevent touch move on sidebar from scrolling the body
  const handleTouchMove = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    const scrollableContent = sidebarContentRef.current;
    
    // If touch is on the scrollable content area, allow it
    if (scrollableContent && scrollableContent.contains(target)) {
      // Check if content is scrollable
      const isScrollable = scrollableContent.scrollHeight > scrollableContent.clientHeight;
      if (isScrollable) {
        // Allow scrolling within the sidebar
        e.stopPropagation();
        return;
      }
    }
    // Otherwise prevent the scroll
    e.preventDefault();
  };

  const handleMoveItem = (itemId: string, direction: 'up' | 'down') => {
    const newMenu = moveSidebarItem(itemId, direction);
    setMenuItems(newMenu);
  };

  const handleToggleVisibility = (itemId: string) => {
    const newMenu = toggleSidebarItemVisibility(itemId);
    setMenuItems(newMenu);
  };

  const handleResetMenu = () => {
    if (confirm('Reset menu to default order?')) {
      const newMenu = resetSidebarMenu();
      setMenuItems(newMenu);
    }
  };

  const hasAnyLink = links && Object.values(links).some(link => link !== '');

  // Render a single menu item
  const renderMenuItem = (item: SidebarMenuItem, index: number) => {
    if (!item.visible && !isEditMode) return null;

    const isFirst = index === 0;
    const isLast = index === menuItems.length - 1;

    // Special styling for AI Vani and Shloka
    const specialClass = item.isSpecial 
      ? item.id === 'ai_vani' 
        ? 'bg-gradient-to-r from-templeRed/10 to-transparent dark:bg-stone-800 hover:from-templeRed/20 border-templeRed/20 text-templeRed dark:text-saffron'
        : 'bg-gradient-to-r from-amber-100/50 to-transparent dark:from-amber-900/20 hover:from-amber-100 border-amber-200/50 text-amber-700 dark:text-amber-400'
      : 'bg-white dark:bg-stone-800 hover:bg-orange-50 dark:hover:bg-stone-700 border-saffron/10 text-templeRed dark:text-saffron';

    const handleClick = () => {
      if (isEditMode) return;
      
      if (item.hasDropdown) {
        if (item.id === 'ebooks') {
          setShowEbookCategories(!showEbookCategories);
        } else if (item.id === 'audios') {
          setShowAudioCategories(!showAudioCategories);
        } else if (item.id === 'album') {
          setShowAlbumCategories(!showAlbumCategories);
        }
      } else if (item.externalLink) {
        // Open external link in new tab
        window.open(item.externalLink, '_blank', 'noopener,noreferrer');
        onClose();
      } else if (item.id === 'ekadashi') {
        // Open Ekadashi calendar modal
        onOpenEkadashi?.();
        onClose();
      } else if (item.tab) {
        onNavigate(item.tab);
        onClose();
      }
    };

    return (
      <div key={item.id} className={`${!item.visible ? 'opacity-40' : ''}`}>
        <div className="flex items-center gap-1">
          {/* Edit mode controls */}
          {isEditMode && isAdmin && (
            <div className="flex flex-col gap-0.5 mr-1">
              <button
                onClick={() => handleMoveItem(item.id, 'up')}
                disabled={isFirst}
                className={`p-1 rounded transition-colors ${isFirst ? 'text-gray-300 dark:text-stone-600' : 'text-saffron hover:bg-saffron/20'}`}
                data-testid={`move-up-${item.id}`}
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => handleMoveItem(item.id, 'down')}
                disabled={isLast}
                className={`p-1 rounded transition-colors ${isLast ? 'text-gray-300 dark:text-stone-600' : 'text-saffron hover:bg-saffron/20'}`}
                data-testid={`move-down-${item.id}`}
              >
                <ArrowDown size={14} />
              </button>
            </div>
          )}

          {/* Main button */}
          <button 
            onClick={handleClick}
            data-testid={`sidebar-${item.id}`}
            className={`flex-1 flex items-center ${item.hasDropdown ? 'justify-between' : 'gap-3'} p-3 rounded-xl border dark:border-stone-700 transition-all font-bold shadow-sm ${specialClass}`}
          >
            <div className="flex items-center gap-3">
              {iconMap[item.icon]}
              <span>{getItemLabel(item)}</span>
              {item.externalLink && <ExternalLink size={12} className="opacity-50" />}
            </div>
            {item.hasDropdown && (
              (item.id === 'ebooks' ? showEbookCategories : item.id === 'audios' ? showAudioCategories : showAlbumCategories) 
                ? <ChevronUp size={16} /> 
                : <ChevronDown size={16} />
            )}
          </button>

          {/* Visibility toggle in edit mode */}
          {isEditMode && isAdmin && (
            <button
              onClick={() => handleToggleVisibility(item.id)}
              className={`p-2 rounded-lg transition-colors ${item.visible ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-stone-800'}`}
              data-testid={`toggle-visibility-${item.id}`}
            >
              {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          )}
        </div>

        {/* Dropdown content for ebooks */}
        {item.id === 'ebooks' && showEbookCategories && !isEditMode && (
          <div className="pl-4 pr-1 py-2 space-y-1 animate-fade-in">
            <button 
              onClick={() => { onNavigateEbook(null); onClose(); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-saffron/5 dark:hover:bg-stone-800 text-left text-xs font-bold text-gray-600 dark:text-stone-400 transition-colors"
            >
              <Book size={14} className="text-saffron/60" />
              <span>All Books</span>
            </button>
            {ebookCategories.map((cat) => (
              <button 
                key={cat}
                onClick={() => { onNavigateEbook(cat); onClose(); }}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-saffron/5 dark:hover:bg-stone-800 text-left text-xs font-bold text-gray-600 dark:text-stone-400 transition-colors"
              >
                <Book size={14} className="text-saffron/60 shrink-0" />
                <span className="line-clamp-2">{cat}</span>
              </button>
            ))}
          </div>
        )}

        {/* Dropdown content for audios */}
        {item.id === 'audios' && showAudioCategories && !isEditMode && (
          <div className="pl-4 pr-1 py-2 space-y-1 animate-fade-in">
            <button 
              onClick={() => { onNavigateAudio(null); onClose(); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-saffron/5 dark:hover:bg-stone-800 text-left text-xs font-bold text-gray-600 dark:text-stone-400 transition-colors"
            >
              <PlayCircle size={14} className="text-saffron/60" />
              <span>All Recordings</span>
            </button>
            {AUDIO_CATEGORIES.map((cat) => (
              <button 
                key={cat}
                onClick={() => { onNavigateAudio(cat); onClose(); }}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-saffron/5 dark:hover:bg-stone-800 text-left text-xs font-bold text-gray-600 dark:text-stone-400 transition-colors"
              >
                <PlayCircle size={14} className="text-saffron/60 shrink-0" />
                <span className="line-clamp-2">{cat}</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Album Categories Dropdown */}
        {item.id === 'album' && showAlbumCategories && !isEditMode && (
          <div className="pl-4 pr-1 py-2 space-y-1 animate-fade-in">
            <button 
              onClick={() => { onNavigateAlbum?.(null); onClose(); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-saffron/5 dark:hover:bg-stone-800 text-left text-xs font-bold text-gray-600 dark:text-stone-400 transition-colors"
            >
              <ImageIcon size={14} className="text-saffron/60" />
              <span>All Photos</span>
            </button>
            {albumCategories.sort((a, b) => a.order - b.order).map((cat) => (
              <button 
                key={cat.id}
                onClick={() => { onNavigateAlbum?.(cat.id); onClose(); }}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-saffron/5 dark:hover:bg-stone-800 text-left text-xs font-bold text-gray-600 dark:text-stone-400 transition-colors"
              >
                <Folder size={14} className="text-saffron/60 shrink-0" />
                <span className="line-clamp-2">{cat.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop Overlay */}
      <div 
        className={`fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* Sidebar Drawer */}
      <div 
        className={`fixed inset-y-0 left-0 z-[210] w-80 bg-cream-dark dark:bg-stone-900 transform transition-transform duration-300 ease-in-out shadow-2xl ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        onTouchMove={handleTouchMove}
      >
        <div className="h-full flex flex-col overflow-hidden">
          
          {/* Header */}
          <div className="p-4 bg-templeRed dark:bg-stone-800 text-cream flex justify-between items-center border-b dark:border-stone-700 shrink-0">
            <h2 className="font-serif font-bold text-lg">{t('nav_menu_seva')}</h2>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button 
                  onClick={() => setIsEditMode(!isEditMode)} 
                  className={`p-1.5 rounded transition-colors ${isEditMode ? 'bg-white/20' : 'hover:bg-red-800 dark:hover:bg-stone-700'}`}
                  data-testid="sidebar-edit-mode-btn"
                  title="Arrange Menu Items"
                >
                  <Settings size={18} />
                </button>
              )}
              <button onClick={onClose} className="hover:bg-red-800 dark:hover:bg-stone-700 p-1 rounded transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Edit Mode Banner */}
          {isEditMode && isAdmin && (
            <div className="bg-amber-100 dark:bg-amber-900/30 p-3 border-b border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <Settings size={16} />
                  <span className="text-xs font-bold uppercase">Edit Mode</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResetMenu}
                    className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1"
                    data-testid="reset-menu-btn"
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                  <button
                    onClick={() => setIsEditMode(false)}
                    className="text-xs font-bold bg-amber-500 text-white px-3 py-1 rounded-full hover:bg-amber-600"
                    data-testid="done-edit-btn"
                  >
                    Done
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-amber-600 dark:text-amber-500 mt-1">
                Use arrows to reorder, eye icon to show/hide
              </p>
            </div>
          )}

          {/* Content */}
          <div 
            ref={sidebarContentRef}
            className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y'
            }}
          >
            
            {/* Navigation Links */}
            <div className="space-y-2">
              {menuItems.map((item, index) => renderMenuItem(item, index))}

              {/* Admin Panel Access - Always at bottom, not reorderable */}
              {isAdmin && !isEditMode && (
                <button 
                  data-testid="sidebar-admin-btn"
                  onClick={() => { onNavigate(Tab.ADMIN); onClose(); }}
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-transparent dark:bg-stone-800 hover:from-amber-500/20 border border-amber-400/30 dark:border-stone-700 transition-all text-amber-700 dark:text-amber-400 font-bold shadow-sm"
                >
                  <Lock size={20} className="text-amber-500" />
                  <div className="text-left">
                    <span className="block">Admin Panel</span>
                    <span className="text-[10px] font-black uppercase tracking-tighter opacity-60">Manage App Settings</span>
                  </div>
                </button>
              )}
            </div>

            {!isEditMode && (
              <>
                <hr className="border-saffron/20 dark:border-stone-700" />

                {/* Bhajan Music Section */}
                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => setShowMusicPlayer(!showMusicPlayer)}
                    className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200/50 dark:border-purple-800/30 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-900/30 dark:hover:to-pink-900/30 transition-all"
                    data-testid="music-section-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${isPlaying ? 'bg-purple-500 text-white' : 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300'}`}>
                        {isPlaying ? <Music2 size={18} className="animate-pulse" /> : <Music size={18} />}
                      </div>
                      <div className="text-left">
                        <span className="block font-bold text-purple-700 dark:text-purple-300">
                          {language === 'hi' ? 'भजन संगीत' : 'Bhajan Music'}
                        </span>
                        {currentTrack && (
                          <span className="text-[10px] text-purple-500 dark:text-purple-400 truncate block max-w-[150px]">
                            {isPlaying ? '♪ ' : ''}{language === 'hi' ? currentTrack.nameHi : currentTrack.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronDown size={16} className={`text-purple-400 transition-transform ${showMusicPlayer ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Music Player Dropdown */}
                  {showMusicPlayer && (
                    <div className="bg-white dark:bg-stone-800 rounded-xl border border-purple-200/50 dark:border-stone-700 p-4 space-y-4 animate-fade-in">
                      
                      {/* Now Playing */}
                      {currentTrack && (
                        <div className="text-center pb-3 border-b border-purple-100 dark:border-stone-700">
                          <p className="text-[10px] uppercase tracking-widest text-purple-400 mb-1">
                            {language === 'hi' ? 'अभी बज रहा है' : 'Now Playing'}
                          </p>
                          <p className="font-bold text-purple-700 dark:text-purple-300 text-sm">
                            {language === 'hi' ? currentTrack.nameHi : currentTrack.name}
                          </p>
                        </div>
                      )}

                      {/* Playback Controls */}
                      <div className="flex items-center justify-center gap-4">
                        <button
                          onClick={prevTrack}
                          className="p-2 rounded-full hover:bg-purple-100 dark:hover:bg-stone-700 text-purple-500 transition-colors"
                          data-testid="music-prev-btn"
                        >
                          <SkipBack size={20} />
                        </button>
                        <button
                          onClick={toggle}
                          className={`p-4 rounded-full transition-all ${
                            isPlaying 
                              ? 'bg-purple-500 text-white shadow-lg shadow-purple-300 dark:shadow-purple-900' 
                              : 'bg-purple-100 dark:bg-stone-700 text-purple-600 dark:text-purple-300'
                          }`}
                          data-testid="music-play-pause-btn"
                        >
                          {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
                        </button>
                        <button
                          onClick={nextTrack}
                          className="p-2 rounded-full hover:bg-purple-100 dark:hover:bg-stone-700 text-purple-500 transition-colors"
                          data-testid="music-next-btn"
                        >
                          <SkipForward size={20} />
                        </button>
                      </div>

                      {/* Volume Control */}
                      <div className="flex items-center gap-3 px-2">
                        <VolumeX size={16} className="text-purple-400 flex-shrink-0" />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={volume}
                          onChange={(e) => setVolume(parseInt(e.target.value))}
                          className="flex-1 h-1.5 bg-purple-100 dark:bg-stone-700 rounded-full appearance-none cursor-pointer accent-purple-500"
                          data-testid="music-volume-slider"
                        />
                        <Volume2 size={16} className="text-purple-400 flex-shrink-0" />
                      </div>

                      {/* Loop Toggle */}
                      <button
                        onClick={() => setLooping(!isLooping)}
                        className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-colors ${
                          isLooping 
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300' 
                            : 'bg-gray-100 dark:bg-stone-700 text-gray-500'
                        }`}
                        data-testid="music-loop-btn"
                      >
                        <Repeat size={14} />
                        {language === 'hi' ? (isLooping ? 'लूप चालू' : 'लूप बंद') : (isLooping ? 'Loop On' : 'Loop Off')}
                      </button>

                      {/* Track List */}
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">
                          {language === 'hi' ? 'सभी भजन' : 'All Tracks'}
                        </p>
                        {tracks.map((track) => (
                          <button
                            key={track.id}
                            onClick={() => setTrack(track)}
                            className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                              currentTrack?.id === track.id 
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                                : 'hover:bg-gray-50 dark:hover:bg-stone-700 text-gray-600 dark:text-gray-300'
                            }`}
                            data-testid={`music-track-${track.id}`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                              currentTrack?.id === track.id 
                                ? 'bg-purple-500 text-white' 
                                : 'bg-gray-100 dark:bg-stone-600 text-gray-400'
                            }`}>
                              {currentTrack?.id === track.id && isPlaying ? (
                                <Music2 size={12} className="animate-pulse" />
                              ) : (
                                <Play size={10} className="ml-0.5" />
                              )}
                            </div>
                            <span className="text-xs truncate">
                              {language === 'hi' ? track.nameHi : track.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <hr className="border-saffron/20 dark:border-stone-700" />

                {/* Quick Features */}
                <div className="space-y-2 pt-2">
                  <h3 className="font-bold text-templeRed dark:text-saffron font-serif text-sm">{t('sidebar_sadhana_features')}</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => { onOpenBadges?.(); onClose(); }}
                      className="flex flex-col items-center gap-1 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                    >
                      <span className="text-lg">🏆</span>
                      <span className="text-[10px] text-gray-600 dark:text-gray-400">{t('sidebar_badges')}</span>
                    </button>
                    <button
                      onClick={() => { onOpenCommunity?.(); onClose(); }}
                      className="flex flex-col items-center gap-1 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                    >
                      <span className="text-lg">👥</span>
                      <span className="text-[10px] text-gray-600 dark:text-gray-400">{t('sidebar_community')}</span>
                    </button>
                    <button
                      onClick={() => { onOpenReminders?.(); onClose(); }}
                      className="flex flex-col items-center gap-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <span className="text-lg">🔔</span>
                      <span className="text-[10px] text-gray-600 dark:text-gray-400">{t('sidebar_reminders')}</span>
                    </button>
                  </div>
                </div>

                {/* Guidelines removed */}
              </>
            )}
          </div>

          {/* Footer with Social Icons */}
          {!isEditMode && (
            <div className="bg-orange-50 dark:bg-stone-950 border-t border-saffron/10 dark:border-stone-800 shrink-0 pb-2">
              {/* PWA Install Button */}
              <PWAInstallButton variant="sidebar" language={language} />
              
              {hasAnyLink && (
                <div className="flex justify-center items-center gap-2 pt-2.5 px-3" data-testid="sidebar-social-icons">
                  {links?.facebook && (
                    <a href={links.facebook} target="_blank" rel="noopener noreferrer" className="text-templeRed/60 dark:text-stone-500 hover:text-templeRed dark:hover:text-saffron hover:scale-110 transition-all p-1.5 bg-white dark:bg-stone-900 rounded-md border border-saffron/10 dark:border-stone-800" title="Facebook">
                      <Facebook size={14} />
                    </a>
                  )}
                  {links?.instagram && (
                    <a href={links.instagram} target="_blank" rel="noopener noreferrer" className="text-templeRed/60 dark:text-stone-500 hover:text-templeRed dark:hover:text-saffron hover:scale-110 transition-all p-1.5 bg-white dark:bg-stone-900 rounded-md border border-saffron/10 dark:border-stone-800" title="Instagram">
                      <Instagram size={14} />
                    </a>
                  )}
                  {links?.youtube && (
                    <a href={links.youtube} target="_blank" rel="noopener noreferrer" className="text-templeRed/60 dark:text-stone-500 hover:text-templeRed dark:hover:text-saffron hover:scale-110 transition-all p-1.5 bg-white dark:bg-stone-900 rounded-md border border-saffron/10 dark:border-stone-800" title="YouTube">
                      <Youtube size={14} />
                    </a>
                  )}
                  {links?.twitter && (
                    <a href={links.twitter} target="_blank" rel="noopener noreferrer" className="text-templeRed/60 dark:text-stone-500 hover:text-templeRed dark:hover:text-saffron hover:scale-110 transition-all p-1.5 bg-white dark:bg-stone-900 rounded-md border border-saffron/10 dark:border-stone-800" title="Twitter">
                      <Twitter size={14} />
                    </a>
                  )}
                  {links?.whatsapp && (
                    <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" className="text-templeRed/60 dark:text-stone-500 hover:text-templeRed dark:hover:text-saffron hover:scale-110 transition-all p-1.5 bg-white dark:bg-stone-900 rounded-md border border-saffron/10 dark:border-stone-800" title="WhatsApp Group">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.94 3.659 1.437 5.63 1.438h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  )}
                  {links?.telegram && (
                    <a href={links.telegram} target="_blank" rel="noopener noreferrer" className="text-templeRed/60 dark:text-stone-500 hover:text-templeRed dark:hover:text-saffron hover:scale-110 transition-all p-1.5 bg-white dark:bg-stone-900 rounded-md border border-saffron/10 dark:border-stone-800" title="Telegram">
                      <Send size={14} />
                    </a>
                  )}
                </div>
              )}
              <div className="py-1.5 text-center text-[9px] text-gray-400 dark:text-stone-600 font-bold uppercase tracking-wider">
                Golok Prem Ras Technology
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
